import * as React from 'react'
import { Select as AntSelect, message, Radio, Input, Checkbox, Tooltip, Icon } from 'antd'
import { browserHistory } from 'react-router'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { DateFilter } from '../../DashboardReports/Financial/ReportFilter/DateFilter'
import { ExternalSourceAPI, ExternalSourceModels } from '@getgreenline/external-sources'
import ProductDropdown from './components/ProductDropdown/ProductDropdown'
import { SelectOption, SelectedProducts } from './FilterGroup.types'
import { SelectFilterDropdown } from './components/FilterDropdown'
import {
  ILocation,
  PaymentType,
  ICustomer,
  PaymentFilter,
  PaymentStatus,
} from '@getgreenline/homi-shared'
import { ProductModels } from '@getgreenline/products'
import { paymentTypeFilterOptions } from '../../../../utilities/paymentsHelper'
import { BLAZE_COMPANY } from '../../../../constants/general'
import { dayjs } from '../../../../utilities/dayjs'

interface FilterGroupProps {
  currentCompanyStore?: CurrentCompanyStore
  filter: PaymentFilter
  selectedLocation: ILocation | undefined
  userPermittedLocations: ILocation[]
  setLocation: (companyId: number, locationId: number) => void
  search: () => void
  setProduct: (product: ProductModels.IBaseProduct, name: string) => void
  selectedProducts: SelectedProducts[]
  removeSelectedProduct: (product: { id: string; name: string }) => Promise<void>
  removeAllProducts: () => Promise<void>
}

interface FilterGroupState {
  externalSources: ExternalSourceModels.ICompanyExternalSourceContract[]
  searchedCustomers: ICustomer[]
  removeLocation: boolean
}

@inject('currentCompanyStore')
@observer
export class FilterGroup extends React.Component<FilterGroupProps, FilterGroupState> {
  constructor(props: FilterGroupProps) {
    super(props)

    this.state = {
      externalSources: [],
      searchedCustomers: [],
      removeLocation: false,
    }
  }

  componentDidMount() {
    this.getExternalSources()
    if (browserHistory.getCurrentLocation().query.showOnlineDiscrepancies) {
      this.setState({ removeLocation: true })
    }
  }

  async getExternalSources() {
    try {
      const externalSources = await ExternalSourceAPI.getCompanyExternalSources(
        this.props.currentCompanyStore!.company!.id,
      )
      this.setState({ externalSources })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'Failed to get external sources')
    }
  }

  loadCustomers = async (input: string) => {
    if (input.length > 2) {
      const searchedCustomers = await this.props.filter.setCustomerSearchQuery(input)
      this.setState({
        searchedCustomers: searchedCustomers,
      })
      return searchedCustomers.map((customer) => ({
        key: customer.id,
        value: customer.id,
        label: customer.name,
      }))
    }

    this.props.filter.searchedCustomers.map((customer: ICustomer) => ({
      key: customer.id,
      value: customer.id,
      label: customer.name,
    }))
  }

  get searchedCustomers() {
    return this.props.filter.searchedCustomers.map((customer) => ({
      value: customer.id,
      label: customer.name,
    }))
  }

  get employeeOptions() {
    return (this.props.currentCompanyStore!.users || []).map((user) => ({
      value: user.id,
      label: user.name,
    }))
  }

  get deviceOptions() {
    return (this.props.currentCompanyStore!.devices || []).map((device) => ({
      value: device.id,
      label: device.deviceId,
    }))
  }

  get statusOptions(): SelectOption[] {
    return [
      { value: 'pending', label: 'Pending' },
      { value: 'in progress', label: 'In progress' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'refunded', label: 'Refunded' },
    ]
  }

  get availableSources(): Array<{ value: number | null; label: string }> {
    const { externalSources } = this.state
    const availableForClickAndCollect = ExternalSourceModels.EXTERNAL_SOURCES_CLICK_AND_COLLECT.map(
      (e) => e.toLowerCase(),
    )
    const betaStrippedExternalSources = externalSources.map((externalSource) => {
      const betaPostfix = ' (BETA)'
      const newExternalSource = { ...externalSource }

      if (newExternalSource.name.toUpperCase().endsWith(betaPostfix)) {
        newExternalSource.name = newExternalSource.name.replace(betaPostfix, '')
      }

      return newExternalSource
    })
    const availableExternalSources = betaStrippedExternalSources
      .filter((externalSource) =>
        availableForClickAndCollect.includes(externalSource.name.toLowerCase()),
      ) // Fitler to only allowed
      .map((externalSource) => ({
        value: externalSource.id || null,
        label: externalSource.name,
      }))

    return [{ value: null, label: BLAZE_COMPANY }, ...availableExternalSources]
  }

  get userLocations() {
    return this.props.userPermittedLocations.map((location: ILocation) => ({
      value: location.id,
      label: location.name,
    }))
  }

  get selectedLocationId(): number | undefined {
    return this.props.selectedLocation?.id
  }

  handleCustomer = (value: string | undefined): void => {
    const customer = this.state.searchedCustomers.find((el: ICustomer) => el.id === value)
    this.props.filter.setCustomer(customer)
  }

  render() {
    const {
      filter,
      setLocation,
      search,
      setProduct,
      selectedProducts,
      removeSelectedProduct,
      removeAllProducts,
    } = this.props

    return (
      <>
        <div className='mb-2' style={{ display: 'flex', flexWrap: 'wrap' }}>
          <SelectFilterDropdown<number>
            label='Location'
            width={180}
            options={this.userLocations}
            placeholder='Select a location'
            value={this.state.removeLocation ? undefined : this.selectedLocationId}
            allowClear={false}
            onChange={(value) => {
              this.setState({ removeLocation: false })
              if (value) {
                setLocation(this.props.currentCompanyStore!.company!.id, value)
              }
            }}
          />

          <div className='mr-2'>
            <label>Dates</label>
            <DateFilter
              startDate={filter.startDate ? filter.startDate.toDate() : null}
              endDate={filter.endDate ? filter.endDate.toDate() : null}
              onStartDateChange={(startDate) => {
                this.props.filter.setStartDate(startDate ? dayjs(startDate) : null)
              }}
              onEndDateChange={(endDate) => {
                this.props.filter.setEndDate(endDate ? dayjs(endDate) : null)
              }}
              allowClear={false}
              maxRangeMonths={12}
            />
          </div>

          <div className='mr-2'>
            <label>Pages</label>
            <div>
              <AntSelect<number>
                className='gr-filter-item gr-filter-gutter'
                value={filter.limit}
                optionLabelProp='label'
                onChange={(value) => {
                  filter.setLimit(value)
                  search()
                }}
              >
                <AntSelect.Option key={20} value={20} label={'20 per page'}>
                  <Radio checked={filter.limit === 20}>20 per page</Radio>
                </AntSelect.Option>
                <AntSelect.Option key={30} value={30} label={'30 per page'}>
                  <Radio checked={filter.limit === 30}>30 per page</Radio>
                </AntSelect.Option>
                <AntSelect.Option key={40} value={40} label={'40 per page'}>
                  <Radio checked={filter.limit === 40}>40 per page</Radio>
                </AntSelect.Option>
                <AntSelect.Option key={50} value={50} label={'50 per page'}>
                  <Radio checked={filter.limit === 50}>50 per page</Radio>
                </AntSelect.Option>
              </AntSelect>
            </div>
          </div>
          <div className='mr-2'>
            <label>
              <span className='mr-1'>Online discrepancies</span>
              <Tooltip
                title={
                  <div>
                    {`Some payments from outside of ${BLAZE_COMPANY} did not provide accurate prices or taxes`}
                  </div>
                }
              >
                <Icon type='question-circle' />
              </Tooltip>
            </label>
            <div
              style={{
                paddingTop: `3px`,
                textAlign: 'center',
                height: `31px`,
                borderStyle: `solid`,
                borderWidth: `1px`,
                borderColor: `#d9d9d9`,
              }}
            >
              <Checkbox
                checked={filter.showOnlineDiscrepancies}
                onChange={() => {
                  filter.setShowOnlineDiscrepancies(!filter.showOnlineDiscrepancies)
                }}
              >
                Discrepancies
              </Checkbox>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <div className='mr-2' style={{ width: 150 }}>
            <label>Sale ID</label>
            <Input value={filter.customId} onChange={(e) => filter.setCustomId(e.target.value)} />
          </div>

          <SelectFilterDropdown<PaymentType>
            label='Payment type'
            width={130}
            options={paymentTypeFilterOptions}
            onChange={(value) => {
              filter.setPaymentType(value)
            }}
          />

          <SelectFilterDropdown<string>
            async
            label='Customer'
            width={200}
            options={this.loadCustomers}
            onChange={this.handleCustomer}
          />

          <SelectFilterDropdown<string>
            label='Employee'
            width={200}
            options={this.employeeOptions}
            onChange={(value) => {
              filter.setEmployeeId(value)
            }}
          />

          <SelectFilterDropdown<string>
            label='POS Device'
            width={200}
            options={this.deviceOptions}
            onChange={(value) => {
              filter.setDeviceId(value)
            }}
          />

          <SelectFilterDropdown<PaymentStatus>
            label='Status'
            width={150}
            options={this.statusOptions}
            onChange={(sourceId) => {
              filter.setStatus(sourceId)
            }}
          />

          <SelectFilterDropdown<number>
            label='Source'
            width={200}
            placeholder='Select a source'
            options={this.availableSources}
            onChange={(sourceId) => filter.setExternalSourceId(sourceId)}
          />
        </div>

        <ProductDropdown
          onSelect={setProduct}
          selectedProducts={selectedProducts}
          removeSelectedProduct={removeSelectedProduct}
          removeAllProducts={removeAllProducts}
        />

        <div style={{ marginTop: '1.5rem' }}>
          <BlazeButton
            buttonProps={{
              type: 'primary',
              onClick: search,
            }}
            titleProps={{ title: 'Apply' }}
          />
        </div>
      </>
    )
  }
}
